/** @jsxImportSource @emotion/react */
import { MapLocation } from '@app/redux/riskMap/riskMapSlice'
import { getBoundsOfFeature } from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import { RegionOption } from '@app/components/Molecules/RegionFilter'
import { Map, MapboxGeoJSONFeature } from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useFetchLayerStyles } from '@app/services/legendService'
import { handleUpdateDrawAreas } from '../utils/drawAreas'
import {
  handleClearMap,
  handleRemoveLayerById,
  handleUpdateLayers,
  RemoveLayerByIdProps,
} from '../utils/layers'
import {
  FitBoundsOfFeatureOptions,
  FitBoundsProps,
  FlyTo,
  handleFitBounds,
  handleFlyTo,
  handleFlyToFeature,
} from '../utils/location'
import { handleChangeMapStyle } from '../utils/style'
import {
  AppInitialLocation,
  ChangeMapStyleProps,
  ClearMapProps,
  GenericMapContextInterface,
  UpdateDrawAreasProps,
  UpdateLayersProps,
} from './GenericMapContext.types'

export const useGenericMapContext = (): GenericMapContextInterface => {
  const dispatch = useDispatch()
  const [map, setMap] = useState<Map | null>(null)
  const [flyToClientLocation, setFlyToClientLocation] = useState(true)
  const [clientLocation, setClientLocation] = useState<MapLocation>(AppInitialLocation)
  const [regionMasks, setRegionMasks] = useState<RegionOption[] | null>(null)
  const { data: mapLayerStyles = {} } = useFetchLayerStyles()
  const [basemap, setBasemap] = useState<string>('Street')
  const [mapIs3D, setMapIs3D] = useState(false)
  const [clientHasChanged, setClientHasChanged] = useState(false)
  const [interactive, setInteractive] = useState(true)
  const [regionFilters, setRegionFilters] = useState<RegionOption[] | null>(null)

  useEffect(() => {
    if (!map) return
    if (interactive) {
      map.dragPan.enable()
      map.scrollZoom.enable()
      map.boxZoom.enable()
      map.dragRotate.enable()
      map.keyboard.enable()
      map.doubleClickZoom.enable()
      map.touchZoomRotate.enable()
    } else {
      map.dragPan.disable()
      map.scrollZoom.disable()
      map.boxZoom.disable()
      map.dragRotate.disable()
      map.keyboard.disable()
      map.doubleClickZoom.disable()
      map.touchZoomRotate.disable()
    }
  }, [interactive, map])

  const mapPaddingRef = useRef<{
    top: number
    right: number
    bottom: number
    left: number
  }>({ top: 0, right: 0, bottom: 0, left: 0 })

  const setMapPadding = (padding: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }) => {
    mapPaddingRef.current = {
      ...mapPaddingRef.current,
      ...padding,
    }
  }

  const getMapPadding = (usePadding: boolean | undefined) =>
    usePadding || usePadding === undefined
      ? mapPaddingRef.current
      : { top: 0, right: 0, bottom: 0, left: 0 }

  const [mapPaddingIsChanging, setMapPaddingIsChanging] = useState({
    top: false,
    right: false,
    bottom: false,
    left: false,
  })

  const handleSetClientLocation = (location: MapLocation) => {
    setClientLocation(location)
  }

  const clearMap = ({ map }: ClearMapProps) => {
    if (!map) return

    handleClearMap({ map })
    if (regionMasks?.length) setRegionMasks(null)
  }

  const changeMapStyle = ({ style }: ChangeMapStyleProps) => {
    handleChangeMapStyle({ style, dispatch })
  }

  const flyTo = (flyTo: FlyTo) => {
    if (!map) return

    handleFlyTo({
      flyTo: { padding: getMapPadding(flyTo.usePadding), ...flyTo },
      map,
    })
  }

  const flyToFeature = (feature: GeoJSON.Feature, flyToOptions?: Partial<FlyTo>) => {
    if (!map) return

    handleFlyToFeature({
      map,
      feature,
      flyToOptions: {
        padding: getMapPadding(flyToOptions?.usePadding),
        ...flyToOptions,
      },
    })
  }

  const fitToBounds = ({ boundingBox, map, usePadding }: FitBoundsProps) => {
    if (!map) return

    handleFitBounds({ boundingBox, map, padding: getMapPadding(usePadding) })
  }

  const fitToBoundsOfFeature = (
    feature: MapboxGeoJSONFeature | GeoJSON.Feature,
    { usePadding }: FitBoundsOfFeatureOptions,
  ) => {
    if (!map) return

    const boundingBox = getBoundsOfFeature(feature)

    if (boundingBox) {
      handleFitBounds({
        boundingBox: [
          boundingBox.min_coord[0],
          boundingBox.min_coord[1],
          boundingBox.max_coord[0],
          boundingBox.max_coord[1],
        ],
        map,
        padding: getMapPadding(usePadding),
      })
    }
  }

  const removeLayerById = ({ layerId, map }: RemoveLayerByIdProps) => {
    if (!map) return

    handleRemoveLayerById({ layerId, map, dispatch })
  }

  const setMapInstance = (map: Map | null) => {
    setMap(map)
  }

  const updateLayers = ({ layers }: UpdateLayersProps) => {
    handleUpdateLayers({ layers, dispatch })
  }

  const updateDrawAreas = ({ drawAreas }: UpdateDrawAreasProps) => {
    handleUpdateDrawAreas({ drawAreas, dispatch })
  }

  useEffect(() => {
    setClientHasChanged(true)
  }, [clientLocation])

  // when the map is ready and the client location is set, fly to the client location
  useEffect(() => {
    if (clientLocation && map && clientHasChanged) {
      if (flyToClientLocation) flyTo({ ...clientLocation, usePadding: true, pitch: 0, bearing: 0 })
      setClientHasChanged(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientHasChanged, map, flyToClientLocation])

  return {
    map,
    regionMasks,
    setRegionMasks,
    mapLayerStyles,
    basemap,
    clientLocation,
    handleSetClientLocation,
    clearMap,
    changeMapStyle,
    flyTo,
    flyToFeature,
    fitToBounds,
    fitToBoundsOfFeature,
    removeLayerById,
    setMapInstance,
    updateLayers,
    updateDrawAreas,
    setMapPadding,
    getMapPadding,
    mapPaddingRef,
    mapPaddingIsChanging,
    setMapPaddingIsChanging,
    mapIs3D,
    setMapIs3D,
    setBasemap,
    setFlyToClientLocation,
    setInteractive,
    interactive,
    regionFilters,
    setRegionFilters,
  }
}
